import * as React from "react"
import { PaintClass, PaintStyle, ToolMode } from "./utils/types"
import { getNextDistinctColor } from "./utils/utils"
import { PaintbrushColorPicker } from "./paintbrush_color_picker"
import { LayerListRow } from "../shared/layer_list_row"
import { ReactSortable } from "react-sortablejs"
import { DatasetLayer, Layer, ModelOutputLayer, PaintbrushGeoJSONLayer } from "../projects/state"
import { iconForLayerType } from "../projects/utils/util"
import { tileGridStats } from "../projects/modelling/tile_grid"
import { PaintbrushLayerInspector } from "./paintbrush_layer_inspector"
import { buildModelDatasetStyleCopyEntriesFromList } from "../projects/layers/layer_style"
import type { LocalGeotiffLayerState } from "../projects/map/local_geotiff_preview"
import { parsePaintbrushLocalGeotiffOrderId } from "./local_geotiff_constants"
import { labelEntriesFromStatsLabels } from "../projects/layers/categorical_layer_colors"
import { mergePaintClassesFromCategoricalLayer } from "./load_classes_from_layer"

/** ReactSortable occasionally emits transient lists with wrong multiset; reject to avoid corrupted layer order state. */
function sameStringIdMultiset(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  for (let i = 0; i < sortedA.length; i++) {
    if (sortedA[i] !== sortedB[i]) return false
  }
  return true
}

enum SidebarTab {
  Paint,
  Layers
}

type PaintbrushSidebarProps = {
  classes: PaintClass[]
  setClasses: React.Dispatch<React.SetStateAction<PaintClass[]>>
  selectedClassId: number | null
  setSelectedClassId: React.Dispatch<React.SetStateAction<number | null>>
  activeTool: ToolMode
  setActiveTool: React.Dispatch<React.SetStateAction<ToolMode>>
  paintStyle: PaintStyle
  setPaintStyle: React.Dispatch<React.SetStateAction<PaintStyle>>
  snapToBoundaryEnabled: boolean
  setSnapToBoundaryEnabled: React.Dispatch<React.SetStateAction<boolean>>
  updateVerticesOnOverlapEnabled: boolean
  setUpdateVerticesOnOverlapEnabled: React.Dispatch<React.SetStateAction<boolean>>
  opacity: number
  setOpacity: React.Dispatch<React.SetStateAction<number>>
  isLoadingGeoJSON?: boolean
  projectLayers: Array<{ id: string; layer: Layer }>
  setProjectLayers: React.Dispatch<React.SetStateAction<Array<{ id: string; layer: Layer }>>>
  layerOrder: string[]
  setLayerOrder: React.Dispatch<React.SetStateAction<string[]>>
  localGeotiffLayers: Record<string, LocalGeotiffLayerState>
  setLocalGeotiffLayers: React.Dispatch<React.SetStateAction<Record<string, LocalGeotiffLayerState>>>
  showLayerPalette: () => void
  osmLayerVisible: boolean
  setOsmLayerVisible: React.Dispatch<React.SetStateAction<boolean>>
  annotationLayerVisible: boolean
  setAnnotationLayerVisible: React.Dispatch<React.SetStateAction<boolean>>
  osmLayerOpacity: number
  setOsmLayerOpacity: React.Dispatch<React.SetStateAction<number>>
  annotationLayerOpacity: number
  setAnnotationLayerOpacity: React.Dispatch<React.SetStateAction<number>>
  annotationId: number
  geojsonUrl?: string
  projectLayerStats: Record<string, tileGridStats>
  onLoadData?: () => void
}

export const PaintbrushSidebar: React.FC<PaintbrushSidebarProps> = ({
  classes,
  setClasses,
  selectedClassId,
  setSelectedClassId,
  activeTool,
  setActiveTool,
  paintStyle,
  setPaintStyle,
  snapToBoundaryEnabled,
  setSnapToBoundaryEnabled,
  updateVerticesOnOverlapEnabled,
  setUpdateVerticesOnOverlapEnabled,
  opacity,
  setOpacity,
  isLoadingGeoJSON = false,
  projectLayers,
  setProjectLayers,
  layerOrder,
  setLayerOrder,
  localGeotiffLayers,
  setLocalGeotiffLayers,
  showLayerPalette,
  osmLayerVisible,
  setOsmLayerVisible,
  annotationLayerVisible,
  setAnnotationLayerVisible,
  osmLayerOpacity,
  setOsmLayerOpacity,
  annotationLayerOpacity,
  setAnnotationLayerOpacity,
  annotationId,
  geojsonUrl = "",
  projectLayerStats,
  onLoadData
}) => {
  const [activeTab, setActiveTab] = React.useState<SidebarTab>(SidebarTab.Paint)
  const [expandedClassId, setExpandedClassId] = React.useState<number | null>(null)
  const [selectedLayerId, setSelectedLayerId] = React.useState<string>("annotations")
  const [isLegCollapsed, setIsLegCollapsed] = React.useState(false)
  const selectedLayerLocalUuid = React.useMemo(
    () => parsePaintbrushLocalGeotiffOrderId(selectedLayerId),
    [selectedLayerId]
  )
  const selectedLocalGeotiff = React.useMemo(
    () =>
      selectedLayerLocalUuid !== null ? localGeotiffLayers[selectedLayerLocalUuid] ?? null : null,
    [selectedLayerLocalUuid, localGeotiffLayers]
  )
  const selectedProjectLayer = React.useMemo(
    () => projectLayers.find(entry => entry.id === selectedLayerId) ?? null,
    [projectLayers, selectedLayerId]
  )

  const modelDatasetStyleEntries = React.useMemo(
    () => buildModelDatasetStyleCopyEntriesFromList(projectLayers, projectLayerStats),
    [projectLayers, projectLayerStats]
  )

  const inspectorSelectedLayer = React.useMemo((): Layer | null => {
    if (selectedLayerLocalUuid !== null) return null
    if (selectedLayerId === "osm") return null
    if (selectedProjectLayer) return selectedProjectLayer.layer
    if (selectedLayerId === "annotations") {
      const paintbrushLayer: PaintbrushGeoJSONLayer = {
        type: "PaintbrushGeoJSONLayer",
        name: "Annotations",
        visible: annotationLayerVisible,
        opacity: annotationLayerOpacity,
        annotationId,
        geojsonUrl,
        legendClasses: classes.map(c => ({ className: c.name, color: c.color }))
      }
      return paintbrushLayer
    }
    return null
  }, [
    selectedLayerId,
    selectedProjectLayer,
    annotationLayerVisible,
    annotationLayerOpacity,
    annotationId,
    geojsonUrl,
    classes,
    selectedLayerLocalUuid
  ])

  const mutateInspectorLayer = React.useCallback(
    (data: Record<string, unknown>) => {
      if (selectedLayerLocalUuid !== null) {
        setLocalGeotiffLayers(prev => {
          const cur = prev[selectedLayerLocalUuid]
          if (!cur) return prev
          const next = { ...cur }
          if (typeof data.name === "string") next.name = data.name
          if (typeof data.opacity === "number") next.opacity = data.opacity
          if (typeof data.visible === "boolean") next.visible = data.visible
          return { ...prev, [selectedLayerLocalUuid]: next }
        })
        return
      }
      if (selectedProjectLayer) {
        setProjectLayers(previous =>
          previous.map(entry =>
            entry.id === selectedProjectLayer.id
              ? { ...entry, layer: { ...entry.layer, ...data } as Layer }
              : entry
          )
        )
        return
      }
      if (selectedLayerId === "annotations") {
        if (typeof data.opacity === "number") {
          setAnnotationLayerOpacity(data.opacity)
          setOpacity(data.opacity)
        }
        if (typeof data.visible === "boolean") {
          setAnnotationLayerVisible(data.visible)
        }
      }
    },
    [
      selectedProjectLayer,
      selectedLayerId,
      setProjectLayers,
      setAnnotationLayerOpacity,
      setOpacity,
      setAnnotationLayerVisible,
      selectedLayerLocalUuid,
      setLocalGeotiffLayers
    ]
  )

  const getPaintbrushLayerData = React.useCallback(
    (_layer: DatasetLayer | ModelOutputLayer): tileGridStats => {
      if (selectedProjectLayer) {
        const stats = projectLayerStats[selectedProjectLayer.id]
        if (stats) return stats
      }
      return {
        min: 0,
        max: 0,
        type: undefined,
        zoom: 0,
        area: 0,
        length: 0
      }
    },
    [selectedProjectLayer, projectLayerStats]
  )

  const selectedLayerOpacity = React.useMemo(() => {
    if (selectedLayerId === "osm") return osmLayerOpacity
    if (selectedLayerId === "annotations") return annotationLayerOpacity
    if (selectedLayerLocalUuid !== null) {
      return localGeotiffLayers[selectedLayerLocalUuid]?.opacity ?? 1
    }
    if (selectedProjectLayer) {
      return selectedProjectLayer.layer.opacity ?? 1
    }
    return 1
  }, [
    selectedLayerId,
    selectedLayerLocalUuid,
    localGeotiffLayers,
    osmLayerOpacity,
    annotationLayerOpacity,
    selectedProjectLayer
  ])

  const setSelectedLayerOpacity = React.useCallback((nextOpacity: number) => {
    if (selectedLayerId === "osm") {
      setOsmLayerOpacity(nextOpacity)
      return
    }
    if (selectedLayerLocalUuid !== null) {
      setLocalGeotiffLayers(prev => {
        const cur = prev[selectedLayerLocalUuid]
        if (!cur) return prev
        return { ...prev, [selectedLayerLocalUuid]: { ...cur, opacity: nextOpacity } }
      })
      return
    }
    if (selectedLayerId === "annotations") {
      setAnnotationLayerOpacity(nextOpacity)
      setOpacity(nextOpacity)
      return
    }
    if (selectedProjectLayer) {
      setProjectLayers(previous =>
        previous.map((entry) =>
          entry.id === selectedProjectLayer.id
            ? { ...entry, layer: { ...entry.layer, opacity: nextOpacity } }
            : entry
        )
      )
    }
  }, [
    selectedLayerId,
    selectedProjectLayer,
    setOsmLayerOpacity,
    setAnnotationLayerOpacity,
    setOpacity,
    setProjectLayers,
    selectedLayerLocalUuid,
    setLocalGeotiffLayers
  ])
  const layerLookup = React.useMemo(() => {
    const lookup = new Map<
      string,
      { kind: "osm" | "annotations" | "project" | "localGeotiff"; layer?: Layer; localId?: string }
    >()
    lookup.set("annotations", { kind: "annotations" })
    lookup.set("osm", { kind: "osm" })
    projectLayers.forEach(entry => {
      lookup.set(entry.id, { kind: "project", layer: entry.layer })
    })
    for (const id of layerOrder) {
      const u = parsePaintbrushLocalGeotiffOrderId(id)
      if (u !== null && localGeotiffLayers[u]) {
        lookup.set(id, { kind: "localGeotiff", localId: u })
      }
    }
    return lookup
  }, [projectLayers, layerOrder, localGeotiffLayers])
  const tabButtonStyle: React.CSSProperties = {
    border: "none",
    borderRadius: 0,
    fontSize: "0.9rem",
    padding: "14px 0 12px",
    backgroundColor: "transparent",
    color: "#8492a6",
    fontWeight: 500,
    letterSpacing: "0.02em",
    borderBottom: "2px solid transparent",
    transition: "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease",
    position: "relative"
  }
  const activeTabButtonStyle: React.CSSProperties = {
    color: "#3b4a87",
    backgroundColor: "#ffffff",
    borderBottom: "2px solid #6471c0",
    fontWeight: 600
  }
  const classRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    borderBottom: "1px solid #eef1f8"
  }
  const selectedClassRowStyle: React.CSSProperties = {
    backgroundColor: "#f5f7ff",
    color: "#1f2d5c"
  }
  const iconButtonStyle: React.CSSProperties = {
    width: "22px",
    height: "22px",
    border: "none",
    background: "transparent",
    color: "#8492a6",
    padding: 0,
    borderRadius: "3px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "11px",
    flexShrink: 0
  }
  const handleAddClass = () => {
    const newClassIndex = classes.length + 1
    const distinctColor = getNextDistinctColor(classes)
    const newClass: PaintClass = { id: Date.now(), name: `Class ${newClassIndex}`, color: distinctColor }
    setClasses(prev => [...prev, newClass])
    setSelectedClassId(newClass.id)
    setExpandedClassId(newClass.id)
  }

  const handleSelectClass = (classId: number) => {
    setSelectedClassId(classId)
    setExpandedClassId(classId)
  }

  const handleUpdateClass = (classId: number, updates: Partial<PaintClass>) => {
    setClasses(prev => {
      const updated = prev.map(paintClass => {
        if (paintClass.id === classId) {
          const newClass = { ...paintClass, ...updates }
          return newClass
        }
        return paintClass
      })
      return updated
    })
  }


  const handleDeleteClass = (classId: number) => {
    setClasses(prev => prev.filter(paintClass => paintClass.id !== classId))
    setSelectedClassId(prev => (prev === classId ? null : prev))
    setExpandedClassId(prev => (prev === classId ? null : prev))
  }

  const handleMoveUp = (classId: number) => {
    setClasses(prev => {
      const updated = [...prev]
      const index = updated.findIndex(paintClass => paintClass.id === classId)
      if (index <= 0) return prev // Already at top or not found
      // Swap elements
      const temp = updated[index - 1]
      updated[index - 1] = updated[index]
      updated[index] = temp
      return updated
    })
  }

  const handleMoveDown = (classId: number) => {
    setClasses(prev => {
      const updated = [...prev]
      const index = updated.findIndex(paintClass => paintClass.id === classId)
      if (index === -1 || index >= updated.length - 1) return prev // Already at bottom or not found
      // Swap elements
      const temp = updated[index]
      updated[index] = updated[index + 1]
      updated[index + 1] = temp
      return updated
    })
  }

  const handleLoadInClasses = React.useCallback(() => {
    if (!selectedProjectLayer || selectedProjectLayer.layer.type !== "DatasetLayer") return
    const stats = projectLayerStats[selectedProjectLayer.id]
    if (!stats || stats.type !== "CategoricalTileGrid" || !stats.labels?.length) return

    const labelEntries = labelEntriesFromStatsLabels(stats.labels)
    if (labelEntries.length === 0) return

    const mergedClasses = mergePaintClassesFromCategoricalLayer(
      classes,
      labelEntries,
      selectedProjectLayer.layer
    )
    setClasses(mergedClasses)
    if (mergedClasses.length > 0 && selectedClassId === null) {
      setSelectedClassId(mergedClasses[0].id)
    }
  }, [classes, projectLayerStats, selectedClassId, selectedProjectLayer, setClasses, setSelectedClassId])

  return (
    <div className="d-flex flex-column" style={{ width: "500px", height: "100%" }}>
      <div style={{ borderBottom: "1px solid #dfe4f4", backgroundColor: "#f8f9fc" }}>
        <div className="d-flex">
          <button
            className="flex-grow-1 paintbrush-sidebar-tab"
            onClick={() => setActiveTab(SidebarTab.Paint)}
            style={{ ...tabButtonStyle, ...(activeTab === SidebarTab.Paint ? activeTabButtonStyle : {}) }}
          >
            <i className="fas fa-paint-brush mr-2" style={{ fontSize: "0.85rem" }} />
            Paint
          </button>
          <button
            className="flex-grow-1 paintbrush-sidebar-tab"
            onClick={() => setActiveTab(SidebarTab.Layers)}
            style={{ ...tabButtonStyle, ...(activeTab === SidebarTab.Layers ? activeTabButtonStyle : {}) }}
          >
            <i className="fas fa-layer-group mr-2" style={{ fontSize: "0.85rem" }} />
            Layers
          </button>
        </div>
        <style>{`
          .paintbrush-sidebar-tab:hover { color: #3b4a87 !important; background-color: #eef2ff !important; }
          .paintbrush-sidebar-tab:focus { outline: none; box-shadow: none; }
          .paintbrush-legend-compact .p-3 { padding: 0.65rem !important; }
          .paintbrush-legend-compact .small,
          .paintbrush-legend-compact .color-bar-label-text,
          .paintbrush-legend-compact .color-bar-legend,
          .paintbrush-legend-compact .color-bar-legend-cat { font-size: 0.78rem !important; }
          .paintbrush-legend-compact .color-bar { margin: 6px 14px 0 !important; }
          .paintbrush-legend-compact .color-bar-item { height: 20px !important; }
          .paintbrush-legend-compact .color-bar-container-cat { padding: 8px !important; max-height: 20vh !important; }
          .paintbrush-legend-compact .color-bar-label { margin-bottom: 2px !important; }
        `}</style>
      </div>
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        {activeTab === SidebarTab.Paint && (
          <div
            className="p-3 d-flex flex-column"
            style={{ height: "100%", minHeight: 0 }}
          >
            <div className="d-flex flex-column" style={{ flex: 2, minHeight: 0 }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0 text-uppercase text-muted" style={{ letterSpacing: "0.05em" }}>
                  Classes
                </h6>
                <div className="d-flex" style={{ gap: "0.5rem" }}>
                  <button 
                    className="btn btn-sm btn-outline-primary" 
                    onClick={handleAddClass}
                    disabled={isLoadingGeoJSON}
                    title={isLoadingGeoJSON ? "Loading saved data, please wait..." : "Add a new class"}
                  >
                    <i className="fas fa-plus mr-1" />
                    New Class
                  </button>
                </div>
                {isLoadingGeoJSON && (
                  <div className="mt-2 text-center">
                    <small className="text-muted">
                      <i className="fas fa-spinner fa-spin mr-1" />
                      Loading saved polygons...
                    </small>
                  </div>
                )}
              </div>
              <div
                className="list-group border rounded flex-grow-1 overflow-auto"
                style={{ minHeight: 0, borderColor: "#dfe4f4" }}
              >
                {classes.map((paintClass, index) => {
                  const isSelected = selectedClassId === paintClass.id
                  const isExpanded = expandedClassId === paintClass.id
                  const isLast = index === classes.length - 1
                  return (
                    <div key={paintClass.id}>
                      <div
                        style={{
                          ...classRowStyle,
                          ...(isSelected ? selectedClassRowStyle : {}),
                          ...(isLast && !isExpanded ? { borderBottom: "none" } : {})
                        }}
                        onClick={() => handleSelectClass(paintClass.id)}
                      >
                        <PaintbrushColorPicker
                          color={paintClass.color}
                          onChange={color => handleUpdateClass(paintClass.id, { color })}
                          size={16}
                          placement="left"
                        />
                        <span
                          className="flex-grow-1 text-truncate"
                          style={{ fontSize: "13px", fontWeight: isSelected ? 600 : 400 }}
                        >
                          {paintClass.name}
                        </span>
                        <button
                          type="button"
                          onMouseDown={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            handleMoveUp(paintClass.id)
                          }}
                          disabled={index === 0}
                          title="Move up"
                          style={{
                            ...iconButtonStyle,
                            opacity: index === 0 ? 0.3 : 1,
                            cursor: index === 0 ? "not-allowed" : "pointer"
                          }}
                        >
                          <i className="fas fa-arrow-up" />
                        </button>
                        <button
                          type="button"
                          onMouseDown={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            handleMoveDown(paintClass.id)
                          }}
                          disabled={index === classes.length - 1}
                          title="Move down"
                          style={{
                            ...iconButtonStyle,
                            opacity: index === classes.length - 1 ? 0.3 : 1,
                            cursor: index === classes.length - 1 ? "not-allowed" : "pointer"
                          }}
                        >
                          <i className="fas fa-arrow-down" />
                        </button>
                        <button
                          type="button"
                          onMouseDown={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            handleDeleteClass(paintClass.id)
                          }}
                          title="Delete"
                          style={{ ...iconButtonStyle, color: "#dc3545" }}
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                      {isExpanded && (
                        <div
                          style={{
                            padding: "8px 10px",
                            background: "#fafbff",
                            borderBottom: isLast ? "none" : "1px solid #eef1f8"
                          }}
                        >
                          <input
                            type="text"
                            className="form-control form-control-sm mb-2"
                            value={paintClass.name}
                            placeholder="Class name"
                            onChange={event => handleUpdateClass(paintClass.id, { name: event.target.value })}
                            onClick={event => event.stopPropagation()}
                          />
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: "8px" }}
                            onClick={event => event.stopPropagation()}
                          >
                            <small className="text-muted" style={{ flexShrink: 0 }}>Opacity</small>
                            <input
                              type="range"
                              className="custom-range"
                              min={0}
                              max={1}
                              step={0.01}
                              value={paintClass.opacity ?? 1}
                              onChange={event => handleUpdateClass(paintClass.id, { opacity: Number(event.target.value) })}
                              aria-label="Class opacity"
                              style={{ flex: 1 }}
                            />
                            <small className="text-muted" style={{ width: "32px", textAlign: "right" }}>
                              {Math.round((paintClass.opacity ?? 1) * 100)}%
                            </small>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
                {classes.length === 0 && <div className="text-muted text-center py-4">No classes yet</div>}
              </div>
            </div>
            <div
              className="pt-3 mt-3"
              style={{ flexShrink: 0, borderTop: "1px solid #dfe4f4" }}
            >
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0 text-uppercase text-muted" style={{ letterSpacing: "0.05em" }}>
                  Tools
                </h6>
              </div>
              <div className="d-flex flex-wrap mb-3" style={{ gap: "0.5rem" }}>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill ${
                    activeTool === ToolMode.Cursor ? "btn-primary" : "btn-outline-secondary"
                  }`}
                  onClick={() => setActiveTool(ToolMode.Cursor)}
                  aria-pressed={activeTool === ToolMode.Cursor}
                  title="Cursor — select, move, edit vertices, split"
                >
                  <i className="fas fa-mouse-pointer mr-1" />
                  Cursor
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill ${
                    activeTool === ToolMode.Paint ? "btn-primary" : "btn-outline-secondary"
                  }`}
                  onClick={() => setActiveTool(ToolMode.Paint)}
                  aria-pressed={activeTool === ToolMode.Paint}
                  title="Paint a polygon"
                >
                  <i className="fas fa-paint-brush mr-1" />
                  Paint
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill ${
                    activeTool === ToolMode.Point ? "btn-primary" : "btn-outline-secondary"
                  }`}
                  onClick={() => setActiveTool(ToolMode.Point)}
                  aria-pressed={activeTool === ToolMode.Point}
                  title="Drop a point annotation with an optional note"
                >
                  <i className="fas fa-map-marker-alt mr-1" />
                  Point
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill ${
                    activeTool === ToolMode.Erase ? "btn-primary" : "btn-outline-secondary"
                  }`}
                  onClick={() => setActiveTool(ToolMode.Erase)}
                  aria-pressed={activeTool === ToolMode.Erase}
                  title="Click a shape to erase it"
                >
                  <i className="fas fa-eraser mr-1" />
                  Erase
                </button>
              </div>
              {activeTool === ToolMode.Paint && (
                <div className="mb-3">
                  <label className="small font-weight-bold text-muted mb-2">Paint Style</label>
                  <div className="btn-group btn-group-sm d-flex" role="group">
                    <button
                      type="button"
                      className={`btn flex-fill ${
                        paintStyle === PaintStyle.Polygon ? "btn-primary" : "btn-outline-secondary"
                      }`}
                      onClick={() => setPaintStyle(PaintStyle.Polygon)}
                      aria-pressed={paintStyle === PaintStyle.Polygon}
                      title="Click to place vertices. Close by clicking the first vertex, Enter or double-click."
                    >
                      <i className="fas fa-draw-polygon mr-1" />
                      Polygon
                    </button>
                    <button
                      type="button"
                      className={`btn flex-fill ${
                        paintStyle === PaintStyle.Freehand ? "btn-primary" : "btn-outline-secondary"
                      }`}
                      onClick={() => setPaintStyle(PaintStyle.Freehand)}
                      aria-pressed={paintStyle === PaintStyle.Freehand}
                      title="Click and drag to paint freely."
                    >
                      <i className="fas fa-pencil-alt mr-1" />
                      Freehand
                    </button>
                  </div>
                </div>
              )}
              <div className="custom-control custom-switch mb-3">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="paintbrush-snap-to-boundary"
                  checked={snapToBoundaryEnabled}
                  onChange={event => setSnapToBoundaryEnabled(event.target.checked)}
                />
                <label
                  className="custom-control-label small"
                  htmlFor="paintbrush-snap-to-boundary"
                  title="Snap drawing/editing to other classes' boundaries so shared borders stay flush."
                >
                  Snap to boundary
                </label>
              </div>
              <div className="custom-control custom-switch mb-3">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="paintbrush-update-vertices-on-overlap"
                  checked={updateVerticesOnOverlapEnabled}
                  onChange={event => setUpdateVerticesOnOverlapEnabled(event.target.checked)}
                />
                <label
                  className="custom-control-label small"
                  htmlFor="paintbrush-update-vertices-on-overlap"
                  title="When drawing over existing classes, update their boundaries by carving out the overlapped area."
                >
                  Update vertices on overlap
                </label>
              </div>
              <div className="mt-3">
                <label className="small font-weight-bold text-muted mb-2 d-flex justify-content-between align-items-center">
                  Global Opacity
                  <span className="badge badge-light text-primary">{Math.round(opacity * 100)}%</span>
                </label>
                <input
                  type="range"
                  className="custom-range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={opacity}
                  onChange={event => {
                    const nextOpacity = Number(event.target.value)
                    setOpacity(nextOpacity)
                    setAnnotationLayerOpacity(nextOpacity)
                  }}
                  aria-label="Fill opacity"
                />
              </div>
            </div>
          </div>
        )}
        {activeTab === SidebarTab.Layers && (
          <div className="d-flex flex-column" style={{ height: "100%", minHeight: 0 }}>
            <div className="d-flex justify-content-end px-3 py-2 bg-light border-bottom">
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={showLayerPalette}>
                <i className="fas fa-plus mr-1" />
                Add layer
              </button>
            </div>
            <div
              className="flex-grow-1 bg-white"
              style={{ overflowY: "auto", flexBasis: "0px" }}
            >
              <ReactSortable
                list={layerOrder.map(id => ({ id }))}
                setList={(list: Array<{ id: string }>) => {
                  const next = list.map(item => item.id)
                  if (!sameStringIdMultiset(layerOrder, next)) return
                  setLayerOrder(next)
                }}
              >
                {layerOrder.map((id) => {
                  const entry = layerLookup.get(id)
                  if (!entry) return null
                  if (entry.kind === "annotations") {
                    return (
                      <div key={id}>
                        <LayerListRow
                          name="Annotations"
                          iconClassName="fa-draw-polygon"
                          iconColor="#6471c0"
                          visible={annotationLayerVisible}
                          selected={selectedLayerId === "annotations"}
                          onSelect={() => setSelectedLayerId("annotations")}
                          onToggleVisibility={(event) => {
                            event.stopPropagation()
                            setAnnotationLayerVisible(previous => !previous)
                          }}
                        />
                      </div>
                    )
                  }
                  if (entry.kind === "osm") {
                    return (
                      <div key={id}>
                        <LayerListRow
                          name="OpenStreetMap"
                          iconClassName="fa-globe"
                          iconColor="#1f7a8c"
                          visible={osmLayerVisible}
                          selected={selectedLayerId === "osm"}
                          onSelect={() => setSelectedLayerId("osm")}
                          onToggleVisibility={(event) => {
                            event.stopPropagation()
                            setOsmLayerVisible(previous => !previous)
                          }}
                          deleteTitle="Remove basemap from stack (you can add OpenStreetMap again from the layer palette)"
                          onDelete={(event) => {
                            event.stopPropagation()
                            setLayerOrder(previous => previous.filter(layerId => layerId !== "osm"))
                            setSelectedLayerId(current => (current === "osm" ? "annotations" : current))
                          }}
                        />
                      </div>
                    )
                  }
                  if (entry.kind === "localGeotiff" && entry.localId) {
                    const local = localGeotiffLayers[entry.localId]
                    if (!local) return null
                    return (
                      <div key={id}>
                        <LayerListRow
                          name={local.name}
                          iconClassName="fa-image"
                          iconColor="#7c3aed"
                          visible={local.visible}
                          selected={selectedLayerId === id}
                          onSelect={() => setSelectedLayerId(id)}
                          onToggleVisibility={(event) => {
                            event.stopPropagation()
                            setLocalGeotiffLayers(prev => {
                              const cur = prev[entry.localId!]
                              if (!cur) return prev
                              return { ...prev, [entry.localId!]: { ...cur, visible: !cur.visible } }
                            })
                          }}
                          deleteTitle="Remove local GeoTIFF preview (not saved)"
                          onDelete={(event) => {
                            event.stopPropagation()
                            setLocalGeotiffLayers(prev => {
                              const { [entry.localId!]: _r, ...rest } = prev
                              return rest
                            })
                            setLayerOrder(previous => previous.filter(layerId => layerId !== id))
                            setSelectedLayerId(current =>
                              current === id ? "annotations" : current
                            )
                          }}
                        />
                      </div>
                    )
                  }
                  const projectLayer = entry.layer!
                  return (
                    <div key={id}>
                      <LayerListRow
                        name={projectLayer.name}
                        iconClassName={iconForLayerType(projectLayer.type)}
                        visible={projectLayer.visible}
                        selected={selectedLayerId === id}
                        onSelect={() => setSelectedLayerId(id)}
                        onToggleVisibility={(event) => {
                          event.stopPropagation()
                          setProjectLayers(previous =>
                            previous.map((item) =>
                              item.id === id
                                ? { ...item, layer: { ...item.layer, visible: !item.layer.visible } }
                                : item
                            )
                          )
                        }}
                        deleteTitle="Remove layer"
                        onDelete={(event) => {
                          event.stopPropagation()
                          setLayerOrder(previous => previous.filter(layerId => layerId !== id))
                          setProjectLayers(previous => previous.filter(item => item.id !== id))
                          setSelectedLayerId(current => (current === id ? "annotations" : current))
                        }}
                      />
                    </div>
                  )
                })}
              </ReactSortable>
            </div>
            {inspectorSelectedLayer !== null ? (
              <div
                className="border-top bg-white paintbrush-legend-compact"
                style={{ flexShrink: 0, maxHeight: "55vh", overflowY: "auto" }}
              >
                <PaintbrushLayerInspector
                  selectedLayer={inspectorSelectedLayer}
                  mutate={mutateInspectorLayer}
                  getLayerData={getPaintbrushLayerData}
                  isLegCollapsed={isLegCollapsed}
                  onToggleLegend={() => setIsLegCollapsed(previous => !previous)}
                  lockLayerName={selectedLayerId === "annotations"}
                  modelDatasetStyleEntries={modelDatasetStyleEntries}
                  styleCopyTargetId={selectedLayerId}
                  onLoadInClasses={handleLoadInClasses}
                />
              </div>
            ) : selectedLocalGeotiff && selectedLayerLocalUuid !== null ? (
              <div
                className="pt-3 mt-3 px-3 pb-3"
                style={{ flexShrink: 0, borderTop: "1px solid #dfe4f4", backgroundColor: "#fff" }}
              >
                <p className="small text-muted mb-2">
                  Local GeoTIFF preview — not saved with the annotation.
                </p>
                <div className="mb-2">
                  <label className="small font-weight-bold text-muted mb-1 d-block">Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={selectedLocalGeotiff.name}
                    onChange={event =>
                      mutateInspectorLayer({ name: event.target.value })
                    }
                  />
                </div>
                <div className="custom-control custom-switch mb-2">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="paintbrush-local-geotiff-visible"
                    checked={selectedLocalGeotiff.visible}
                    onChange={() =>
                      mutateInspectorLayer({ visible: !selectedLocalGeotiff.visible })
                    }
                  />
                  <label className="custom-control-label small" htmlFor="paintbrush-local-geotiff-visible">
                    Visible
                  </label>
                </div>
                <div className="mb-2">
                  <label className="small font-weight-bold text-muted mb-2 d-flex justify-content-between align-items-center">
                    Opacity
                    <span className="badge badge-light text-primary">{Math.round(selectedLayerOpacity * 100)}%</span>
                  </label>
                  <input
                    type="range"
                    className="custom-range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={selectedLayerOpacity}
                    onChange={event => setSelectedLayerOpacity(Number(event.target.value))}
                    aria-label="Layer opacity"
                  />
                </div>
              </div>
            ) : (
              <div
                className="pt-3 mt-3 px-3 pb-3"
                style={{ flexShrink: 0, borderTop: "1px solid #dfe4f4", backgroundColor: "#fff" }}
              >
                <div className="mb-2">
                  <label className="small font-weight-bold text-muted mb-2 d-flex justify-content-between align-items-center">
                    Opacity
                    <span className="badge badge-light text-primary">{Math.round(selectedLayerOpacity * 100)}%</span>
                  </label>
                  <input
                    type="range"
                    className="custom-range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={selectedLayerOpacity}
                    onChange={event => setSelectedLayerOpacity(Number(event.target.value))}
                    aria-label="Layer opacity"
                  />
                </div>
                <p className="small text-muted mb-0">Basemap has no legend or style settings.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}